// src/data/generateShipments.js

import { PRODUCT_PROFILES } from './products';
import { 
  CARRIERS, 
  ORIGINS, 
  DESTINATIONS, 
  getRandomRootCause 
} from './locations';

// =============================================================================
// CONFIGURATION
// =============================================================================

const CONFIG = {
  totalShipments: 500,           // How many shipments to generate
  daysOfHistory: 60,             // Generate shipments from past 60 days
  readingIntervalMinutes: 15,    // Temperature logged every 15 min (industry standard)
  baseExcursionRate: 0.28,       // 28% of shipments have some excursion (industry avg)
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Generate a random date within the past N days
const randomDateInPast = (daysAgo) => {
  const now = new Date();
  const pastDate = new Date(now.getTime() - Math.random() * daysAgo * 24 * 60 * 60 * 1000);
  return pastDate;
};

// Generate a shipment ID like "SHP-2024-00001"
const generateShipmentId = (index) => {
  const year = new Date().getFullYear();
  return `SHP-${year}-${String(index + 1).padStart(5, '0')}`;
};

// Calculate transit hours based on origin/destination distance
const calculateTransitHours = (origin, destination, carrier) => {
  // Simple distance calculation using coordinates
  const latDiff = Math.abs(origin.lat - destination.lat);
  const lngDiff = Math.abs(origin.lng - destination.lng);
  const roughDistance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
  
  // Base hours: 12-72 hours depending on distance
  const baseHours = 12 + (roughDistance * 2);
  
  // Adjust by carrier speed
  const carrierMultiplier = carrier.avgTransitDays / 1.5;
  
  // Add some randomness (±20%)
  const variance = 0.8 + Math.random() * 0.4;
  
  return Math.round(baseHours * carrierMultiplier * variance);
};

// Determine shipment status based on dates
const determineStatus = (shipDate, transitHours, hasExcursion, excursionSeverity) => {
  const now = new Date();
  const expectedDelivery = new Date(shipDate.getTime() + transitHours * 60 * 60 * 1000);
  
  // If expected delivery is in the future, still in transit
  if (expectedDelivery > now) {
    return 'In Transit';
  }
  
  // If critical excursion, product is quarantined
  if (hasExcursion && excursionSeverity === 'Critical') {
    return 'Quarantined';
  }
  
  // If major excursion, might be under review (30% chance)
  if (hasExcursion && excursionSeverity === 'Major' && Math.random() < 0.3) {
    return 'Under Review';
  }
  
  return 'Delivered';
};

// =============================================================================
// TEMPERATURE READING GENERATION
// =============================================================================

const generateTemperatureReadings = (
  shipmentId,
  product,
  shipDate,
  transitHours,
  excursionPlan  // { hasExcursion, severity, startHour, durationMinutes }
) => {
  const readings = [];
  const totalReadings = Math.floor((transitHours * 60) / CONFIG.readingIntervalMinutes);
  
  // Calculate temperature parameters
  const idealTemp = (product.idealMin + product.idealMax) / 2;
  const normalVariance = (product.idealMax - product.idealMin) / 3;
  
  for (let i = 0; i <= totalReadings; i++) {
    const timestamp = new Date(
      shipDate.getTime() + i * CONFIG.readingIntervalMinutes * 60 * 1000
    );
    const hourMark = (i * CONFIG.readingIntervalMinutes) / 60;
    
    // Start with normal temperature (small random variance)
    let temperature = idealTemp + (Math.random() - 0.5) * normalVariance * 2;
    
    // If there's an excursion and we're in the excursion window
    if (excursionPlan.hasExcursion) {
      const excursionEndHour = excursionPlan.startHour + (excursionPlan.durationMinutes / 60);
      
      if (hourMark >= excursionPlan.startHour && hourMark < excursionEndHour) {
        // Calculate how far into the excursion we are (0 to 1)
        const progress = (hourMark - excursionPlan.startHour) / (excursionPlan.durationMinutes / 60);
        
        // Excursion follows a curve: rises, peaks at middle, falls
        const excursionCurve = Math.sin(progress * Math.PI);
        
        // Deviation amount based on severity
        const maxDeviation = 
          excursionPlan.severity === 'Critical' ? 12 :
          excursionPlan.severity === 'Major' ? 6 : 2.5;
        
        // Apply excursion (temperature goes ABOVE max)
        temperature = product.tempMax + (maxDeviation * excursionCurve);
      }
    }
    
    // Determine which leg of journey (for location tracking)
    let location;
    if (hourMark < transitHours * 0.3) {
      location = 'Origin Facility';
    } else if (hourMark < transitHours * 0.7) {
      location = 'In Transit';
    } else {
      location = 'Destination Facility';
    }
    
    readings.push({
      id: `${shipmentId}-R${String(i).padStart(4, '0')}`,
      shipmentId,
      timestamp: timestamp.toISOString(),
      temperatureC: Math.round(temperature * 10) / 10,  // Round to 1 decimal
      location,
      sensorId: `SENS-${shipmentId.slice(-5)}`,
    });
  }
  
  return readings;
};

// =============================================================================
// EXCURSION DETECTION & RECORDING
// =============================================================================

const createExcursionRecord = (
  shipmentId,
  excursionPlan,
  shipDate,
  product,
  units
) => {
  if (!excursionPlan.hasExcursion) return null;
  
  const startTime = new Date(
    shipDate.getTime() + excursionPlan.startHour * 60 * 60 * 1000
  );
  const endTime = new Date(
    startTime.getTime() + excursionPlan.durationMinutes * 60 * 1000
  );
  
  const maxDeviation = 
    excursionPlan.severity === 'Critical' ? 12 :
    excursionPlan.severity === 'Major' ? 6 : 2.5;
  
  const rootCause = getRandomRootCause();
  
  // Calculate value at risk
  const valueAtRisk = units * product.unitValue;
  
  return {
    id: `EXC-${shipmentId.slice(-5)}`,
    shipmentId,
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
    durationMinutes: excursionPlan.durationMinutes,
    maxDeviationC: maxDeviation,
    severity: excursionPlan.severity,
    rootCause: rootCause.name,
    rootCauseId: rootCause.id,
    valueAtRisk,
    // Was product actually lost? Critical = 80% loss, Major = 20% loss, Minor = 0%
    productLost: 
      excursionPlan.severity === 'Critical' ? Math.random() < 0.8 :
      excursionPlan.severity === 'Major' ? Math.random() < 0.2 : false,
  };
};

// =============================================================================
// MAIN GENERATOR FUNCTION
// =============================================================================

export const generateAllData = () => {
  const shipments = [];
  const allReadings = [];
  const allExcursions = [];
  
  console.log(`Generating ${CONFIG.totalShipments} shipments...`);
  
  for (let i = 0; i < CONFIG.totalShipments; i++) {
    // 1. Select random product, carrier, origin, destination
    const product = PRODUCT_PROFILES[Math.floor(Math.random() * PRODUCT_PROFILES.length)];
    const carrier = CARRIERS[Math.floor(Math.random() * CARRIERS.length)];
    const origin = ORIGINS[Math.floor(Math.random() * ORIGINS.length)];
    const destination = DESTINATIONS[Math.floor(Math.random() * DESTINATIONS.length)];
    
    // 2. Generate shipment dates
    const shipDate = randomDateInPast(CONFIG.daysOfHistory);
    const transitHours = calculateTransitHours(origin, destination, carrier);
    
    // 3. Determine if this shipment has an excursion
    // Less reliable carriers have higher excursion rates
    const carrierExcursionRate = CONFIG.baseExcursionRate * (2 - carrier.reliabilityScore);
    const hasExcursion = Math.random() < carrierExcursionRate;
    
    // 4. Plan the excursion details (if any)
    let excursionPlan = { hasExcursion: false };
    
    if (hasExcursion) {
      // Severity distribution: 15% Critical, 35% Major, 50% Minor
      const severityRoll = Math.random();
      const severity = 
        severityRoll < 0.15 ? 'Critical' :
        severityRoll < 0.50 ? 'Major' : 'Minor';
      
      // Excursion duration based on severity
      const durationMinutes = 
        severity === 'Critical' ? 60 + Math.floor(Math.random() * 120) :  // 60-180 min
        severity === 'Major' ? 20 + Math.floor(Math.random() * 40) :      // 20-60 min
        5 + Math.floor(Math.random() * 15);                                // 5-20 min
      
      // Excursion can happen anytime after first 2 hours, before last 2 hours
      const startHour = 2 + Math.random() * (transitHours - 4);
      
      excursionPlan = {
        hasExcursion: true,
        severity,
        durationMinutes,
        startHour,
      };
    }
    
    // 5. Generate shipment ID and units
    const shipmentId = generateShipmentId(i);
    const units = 20 + Math.floor(Math.random() * 230);  // 20-250 units
    
    // 6. Determine status
    const status = determineStatus(shipDate, transitHours, hasExcursion, excursionPlan.severity);
    
    // 7. Calculate stability budget consumed
    let stabilityBudgetConsumed = 0;
    if (hasExcursion) {
      const excursionHours = excursionPlan.durationMinutes / 60;
      stabilityBudgetConsumed = (excursionHours / product.stabilityBudgetHours) * 100;
    }
    
    // 8. Generate temperature readings
    const readings = generateTemperatureReadings(
      shipmentId,
      product,
      shipDate,
      transitHours,
      excursionPlan
    );
    allReadings.push(...readings);
    
    // 9. Create excursion record if applicable
    const excursion = createExcursionRecord(
      shipmentId,
      excursionPlan,
      shipDate,
      product,
      units
    );
    if (excursion) {
      allExcursions.push(excursion);
    }
    
    // 10. Create the shipment record
    const expectedDelivery = new Date(shipDate.getTime() + transitHours * 60 * 60 * 1000);
    
    shipments.push({
      id: shipmentId,
      // Product info
      productId: product.id,
      productType: product.type,
      productName: product.name,
      tempMin: product.tempMin,
      tempMax: product.tempMax,
      stabilityBudgetHours: product.stabilityBudgetHours,
      // Logistics info
      carrierId: carrier.id,
      carrierName: carrier.name,
      originId: origin.id,
      originName: origin.name,
      originState: origin.state,
      originLat: origin.lat,
      originLng: origin.lng,
      destinationId: destination.id,
      destinationName: destination.name,
      destinationState: destination.state,
      destinationLat: destination.lat,
      destinationLng: destination.lng,
      // Dates
      shipDate: shipDate.toISOString(),
      expectedDelivery: expectedDelivery.toISOString(),
      actualDelivery: status === 'Delivered' ? expectedDelivery.toISOString() : null,
      transitHours,
      // Status
      status,
      hasExcursion,
      excursionSeverity: excursionPlan.severity || null,
      stabilityBudgetConsumed: Math.round(stabilityBudgetConsumed * 10) / 10,
      // Quantity & Value
      units,
      unitValue: product.unitValue,
      totalValue: units * product.unitValue,
    });
  }
  
  console.log(`Generated ${shipments.length} shipments`);
  console.log(`Generated ${allReadings.length} temperature readings`);
  console.log(`Generated ${allExcursions.length} excursion events`);
  
  return {
    shipments,
    readings: allReadings,
    excursions: allExcursions,
    generatedAt: new Date().toISOString(),
  };
};

// Export a singleton instance so data is consistent across the app
let cachedData = null;

export const getData = () => {
  if (!cachedData) {
    cachedData = generateAllData();
  }
  return cachedData;
};

// Force regenerate (useful for "refresh" button)
export const regenerateData = () => {
  cachedData = generateAllData();
  return cachedData;
};
